import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { storeFetch } from 'services/medusa/storeFetch';
import { useCustomer } from './CustomerContext';
import { getVariantSpecValue } from 'utils/variantSpec';

// Pedir la opción de cada variante para mostrar el tamaño real ("30 ml")
// en lugar de `variant_title`, que viene inconsistente del backend.
// `customer.has_account` sirve para detectar un carrito ligado a una cuenta sin sesión abierta.
// `promotions.code` + `discount_total`: códigos promocionales aplicados.
const CART_FIELDS = 'fields=%2Bitems.variant.options.value,%2Bitems.variant.options.option.title,%2Bcustomer.id,%2Bcustomer.has_account,%2Bpromotions.code,%2Bdiscount_total';

const CartContext = createContext();

// Requests a Medusa: autenticadas si hay sesión (ver storeFetch.js).
const medusaRequest = storeFetch;

// Cargar carrito local desde localStorage
const loadLocalCart = () => {
  try {
    const saved = localStorage.getItem('physiology_local_cart');
    return saved ? JSON.parse(saved) : [];
  } catch (e) { console.warn('CartContext: error reading local cart', e); return []; }
};

const saveLocalCart = (items) => {
  localStorage.setItem('physiology_local_cart', JSON.stringify(items));
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [localItems, setLocalItems] = useState(loadLocalCart);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [medusaAvailable, setMedusaAvailable] = useState(false);

  // Persistir carrito local
  useEffect(() => {
    saveLocalCart(localItems);
  }, [localItems]);

  // Inicializar carrito al cargar
  useEffect(() => {
    const initializeCart = async () => {
      const savedCartId = localStorage.getItem('medusa_cart_id');

      if (savedCartId) {
        try {
          const data = await medusaRequest(`/store/carts/${savedCartId}?${CART_FIELDS}`);
          if (data.cart && data.cart.completed_at === null) {
            setCart(data.cart);
            setMedusaAvailable(true);
            return;
          }
        } catch (error) {
          console.log('Cart not found, creating new one');
          localStorage.removeItem('medusa_cart_id');
        }
      }

      try {
        const data = await medusaRequest(`/store/carts?${CART_FIELDS}`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        setCart(data.cart);
        setMedusaAvailable(true);
        localStorage.setItem('medusa_cart_id', data.cart.id);
      } catch (error) {
        console.log('Medusa no disponible, usando carrito local');
        setMedusaAvailable(false);
      }
    };

    initializeCart();
  }, []);

  // Mostrar notificación helper
  const showNotification = useCallback((product, variantId) => {
    const variant = product.variants?.find(v => v.id === variantId);
    setNotification({
      productName: product.title || product.name,
      variantLabel: variant?.title || variant?.label,
      thumbnail: product.thumbnail || product.image || null,
      price: variant?.calculated_price?.calculated_amount
        ? variant.calculated_price.calculated_amount
        : variant?.price || product.price
    });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  // Agregar producto al carrito
  const addToCart = useCallback(async (product, variantId, quantity = 1) => {
    setIsCartLoading(true);
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 600));

    try {
      // Intentar con Medusa si está disponible
      if (cart && medusaAvailable) {
        const data = await medusaRequest(`/store/carts/${cart.id}/line-items?${CART_FIELDS}`, {
          method: 'POST',
          body: JSON.stringify({
            variant_id: variantId,
            quantity: quantity,
          }),
        });
        setCart(data.cart);
      } else {
        // Fallback: carrito local
        setLocalItems(prev => {
          const existingIdx = prev.findIndex(
            item => item.productId === product.id && item.variantId === variantId
          );
          if (existingIdx > -1) {
            const updated = [...prev];
            updated[existingIdx] = { ...updated[existingIdx], quantity: updated[existingIdx].quantity + quantity };
            return updated;
          }
          const variant = product.variants?.find(v => v.id === variantId);
          return [...prev, {
            id: `local_${Date.now()}`,
            productId: product.id,
            variantId: variantId,
            name: product.title || product.name,
            image: product.thumbnail || product.image,
            size: getVariantSpecValue(variant),
            price: variant?.price || product.price || 0,
            quantity
          }];
        });
      }

      showNotification(product, variantId);

    } catch (error) {
      console.error('Error adding to cart:', error);
      // Si Medusa falla, usar fallback local
      setLocalItems(prev => {
        const existingIdx = prev.findIndex(
          item => item.productId === product.id && item.variantId === variantId
        );
        if (existingIdx > -1) {
          const updated = [...prev];
          updated[existingIdx] = { ...updated[existingIdx], quantity: updated[existingIdx].quantity + quantity };
          return updated;
        }
        const variant = product.variants?.find(v => v.id === variantId);
        return [...prev, {
          id: `local_${Date.now()}`,
          productId: product.id,
          variantId: variantId,
          name: product.title || product.name,
          image: product.thumbnail || product.image,
          size: getVariantSpecValue(variant),
          price: variant?.price || product.price || 0,
          quantity
        }];
      });
      showNotification(product, variantId);
    } finally {
      await minLoadingTime;
      setIsCartLoading(false);
    }
  }, [cart, medusaAvailable, showNotification]);

  // Reordenar: agrega de golpe todas las líneas de un pedido previo al carrito.
  // Omite regalos de distribuidora (metadata.distributor_gift) — esos se
  // vuelven a agregar solos al alcanzar el tier en el checkout. Devuelve el
  // número de líneas agregadas.
  const reorder = useCallback(async (items = []) => {
    const payable = items.filter(
      (it) => !(it?.metadata && it.metadata.distributor_gift)
    );
    const lines = payable
      .map((it) => ({ variant_id: it.variant_id, quantity: it.quantity || 1 }))
      .filter((l) => l.variant_id);
    if (!lines.length) return 0;

    setIsCartLoading(true);
    try {
      if (cart && medusaAvailable) {
        let latest = cart;
        for (const line of lines) {
          const data = await medusaRequest(`/store/carts/${cart.id}/line-items?${CART_FIELDS}`, {
            method: 'POST',
            body: JSON.stringify(line),
          });
          latest = data.cart;
        }
        setCart(latest);
      } else {
        for (const it of payable) {
          await addToCart(
            {
              id: it.product_id,
              title: it.product_title || it.title,
              thumbnail: it.thumbnail,
            },
            it.variant_id,
            it.quantity || 1
          );
        }
      }
      return lines.length;
    } catch (error) {
      console.error('Error en reorder:', error);
      throw error;
    } finally {
      setIsCartLoading(false);
    }
  }, [cart, medusaAvailable, addToCart]);

  // Eliminar item del carrito
  const removeItem = useCallback(async (itemId) => {
    // Check if it's a local item
    if (String(itemId).startsWith('local_')) {
      setLocalItems(prev => prev.filter(item => item.id !== itemId));
      return;
    }
    if (!cart) return;

    try {
      const data = await medusaRequest(`/store/carts/${cart.id}/line-items/${itemId}?${CART_FIELDS}`, {
        method: 'DELETE',
      });
      // DELETE responde { id, deleted, parent } — el carrito viene en `parent`.
      setCart(data.parent || data.cart);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }, [cart]);

  // Actualizar cantidad de un item
  const updateQuantity = useCallback(async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }

    // Check if it's a local item
    if (String(itemId).startsWith('local_')) {
      setLocalItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ));
      return;
    }
    if (!cart) return;

    try {
      const data = await medusaRequest(`/store/carts/${cart.id}/line-items/${itemId}?${CART_FIELDS}`, {
        method: 'POST',
        body: JSON.stringify({ quantity: newQuantity }),
      });
      setCart(data.cart);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }, [cart, removeItem]);

  // Códigos promocionales (promociones de Medusa). Si el código no existe,
  // ya se usó o no aplica, Medusa no lo agrega: se avisa en vez de fallar callado.
  const applyPromoCode = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim().toUpperCase();
    if (!code) throw new Error('Escribe un código.');
    if (!cart) throw new Error('El carrito aún no está listo, intenta de nuevo.');
    let data;
    try {
      data = await medusaRequest(`/store/carts/${cart.id}/promotions?${CART_FIELDS}`, {
        method: 'POST',
        body: JSON.stringify({ promo_codes: [code] }),
      });
    } catch (e) {
      // El backend explica cuando el cupón no aplica a distribuidoras o ya hay otro aplicado.
      throw new Error(/distribuidora|un cupón/i.test(e?.message || '') ? e.message : 'Este código no es válido o ya fue usado.');
    }
    setCart(data.cart);
    const applied = (data.cart?.promotions || []).some((p) => p?.code?.toUpperCase() === code);
    if (!applied) throw new Error('Este código no es válido o ya fue usado.');
  }, [cart]);

  const removePromoCode = useCallback(async (code) => {
    if (!cart) return;
    const data = await medusaRequest(`/store/carts/${cart.id}/promotions?${CART_FIELDS}`, {
      method: 'DELETE',
      body: JSON.stringify({ promo_codes: [code] }),
    });
    setCart(data.cart);
  }, [cart]);

  // Limpiar carrito
  const clearCart = useCallback(async () => {
    setLocalItems([]);
    try {
      localStorage.removeItem('medusa_cart_id');
      const data = await medusaRequest(`/store/carts?${CART_FIELDS}`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setCart(data.cart);
      localStorage.setItem('medusa_cart_id', data.cart.id);
    } catch (error) {
      console.log('Medusa no disponible, carrito local limpiado');
    }
  }, []);

  // ─── Carrito ↔ sesión ──────────────────────────────────────────────────────
  // Con sesión: el carrito se transfiere a la clienta (precio socia correcto).
  // Sin sesión: si el carrito quedó ligado a una CUENTA (p. ej. tras cerrar
  // sesión), se reemplaza por uno de invitada con los mismos productos, para
  // no seguir mostrando precio socia / mínimo de distribuidora.
  const { token, customer } = useCustomer() || {};
  const syncingRef = useRef(false);
  useEffect(() => {
    if (!cart?.id || syncingRef.current) return;
    const linkedToAccount = Boolean(cart.customer?.has_account);
    const needsTransfer = token && customer?.id && cart.customer_id !== customer.id;
    const needsGuestCart = !token && linkedToAccount;
    if (!needsTransfer && !needsGuestCart) return;

    syncingRef.current = true;
    (async () => {
      try {
        if (needsTransfer) {
          const data = await medusaRequest(`/store/carts/${cart.id}/customer?${CART_FIELDS}`, { method: 'POST' });
          setCart(data.cart);
          return;
        }
        const items = (cart.items || []).map((i) => ({ variant_id: i.variant_id, quantity: i.quantity }));
        let { cart: fresh } = await medusaRequest(`/store/carts?${CART_FIELDS}`, {
          method: 'POST',
          body: JSON.stringify({}),
        });
        for (const item of items) {
          try {
            ({ cart: fresh } = await medusaRequest(`/store/carts/${fresh.id}/line-items?${CART_FIELDS}`, {
              method: 'POST',
              body: JSON.stringify(item),
            }));
          } catch (e) {
            console.warn('No se pudo pasar un producto al carrito de invitada', e);
          }
        }
        localStorage.setItem('medusa_cart_id', fresh.id);
        setCart(fresh);
      } catch (error) {
        console.error('Error sincronizando carrito con la sesión:', error);
      } finally {
        syncingRef.current = false;
      }
    })();
  }, [token, customer?.id, cart]);

  // Mapear items de Medusa a formato UI
  const medusaItems = cart?.items?.map(item => ({
    id: item.id,
    productId: item.product_id,
    variantId: item.variant_id,
    name: item.product_title || item.title,
    image: item.thumbnail,
    size: getVariantSpecValue(item.variant),
    price: item.unit_price || 0,
    quantity: item.quantity
  })) || [];

  // Combinar items de Medusa + locales
  const cartItems = [...medusaItems, ...localItems];

  // Calcular totales
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const localSubtotal = localItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  // Medusa v2: montos en pesos (unidades mayores), sin dividir.
  // OJO: en v2 `cart.subtotal` INCLUYE el envío; el de solo productos es `item_subtotal`.
  const subtotal = (cart?.item_subtotal ?? 0) + localSubtotal;
  const shipping = cart?.shipping_total ? cart.shipping_total : (cartItems.length > 0 ? 99 : 0);
  const total = cart?.total ? (cart.total + localSubtotal) : subtotal + shipping;
  const discount = Number(cart?.discount_total) || 0;
  // Las ofertas por producto son automáticas (2X1-, 3X2-, DESC-, con VIP): no se
  // muestran como cupón quitable.
  const OFERTA = /^(2X1|3X2|DESC)(VIP)?-/i;
  // Una promoción borrada en el admin llega como null en el carrito: se ignora.
  const allPromoCodes = (cart?.promotions || []).map((p) => p?.code).filter(Boolean);
  const promoCodes = allPromoCodes.filter((c) => !OFERTA.test(c));
  const has2x1 = allPromoCodes.length > promoCodes.length;
  // Etiquetas de las ofertas aplicadas: "2x1", "3x2", "Descuento".
  const ofertasAplicadas = [...new Set(allPromoCodes.filter((c) => OFERTA.test(c)).map((c) => {
    const t = c.toUpperCase().match(OFERTA)[1];
    return t === 'DESC' ? 'Descuento' : t.toLowerCase();
  }))];

  return (
    <CartContext.Provider value={{
      cart,
      cartItems,
      cartCount,
      subtotal,
      shipping,
      total,
      discount,
      promoCodes,
      has2x1,
      ofertasAplicadas,
      applyPromoCode,
      removePromoCode,
      isCartLoading,
      addToCart,
      reorder,
      updateQuantity,
      removeItem,
      clearCart,
      notification
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
